import { useI18N, MultiLangText } from "@customer-tools-web/component-i18n";
import { useMarketConfig } from "@customer-tools-web/market-extension-resolver";
import * as React from "react";
import { useApolloClient } from "react-apollo";
import { ThemeContext } from "styled-components";

import {
  ListingsOfCacheQuery,
  ListingsOfCacheQueryVariables,
  ListingOfCacheQuery,
  ListingOfCacheQueryVariables,
} from "../../../__generated__/listings";
import { Modal, ModalProps } from "@customer-tools-web/component-ui";
import { LISTING_OF_CACHE_QUERY, LISTINGS_OF_CACHE_QUERY } from "../queries";
import { addSplit } from "../tools";

// see https://reactjs.org/docs/hooks-faq.html#what-can-i-do-if-my-effect-dependencies-change-too-often
export function useLatestState<S = any>(state: S) {
  const ref = React.useRef(state);
  React.useEffect(() => {
    ref.current = state;
  }, [state]);
  return ref;
}

export function useTheme() {
  return React.useContext(ThemeContext);
}

const ref = React.createRef<HTMLDivElement>();
const mainContentRefContext = React.createContext(ref);
export function useMainContentRef() {
  return React.useContext(mainContentRefContext);
}

export function scrollToAnchor(anchorName: string) {
  if (anchorName) {
    const anchorElement = document.getElementById(anchorName);
    if (anchorElement) {
      setTimeout(() => {
        anchorElement.scrollIntoView({ behavior: "smooth" });
      });
    }
  }
}

interface LevelType {
  id: string | null;
  text: MultiLangText | null;
}

export const useLocationAddress = () => {
  const { parseToLocaleText } = useI18N();
  return (
    location?: {
      level1: LevelType | null;
      level2: LevelType | null;
      level3: LevelType | null;
      level5: LevelType | null;
      postalCode: string | null;
      address: MultiLangText | null;
    } | null
  ) => {
    if (!location) {
      return "";
    }

    const address = [
      parseToLocaleText(location.address),
      parseToLocaleText(location.level3?.text),
      parseToLocaleText(location.level2?.text),
      location.postalCode,
      parseToLocaleText(location.level1?.text),
    ];

    const level5Text = parseToLocaleText(location.level5?.text);
    address.unshift(
      level5Text ? level5Text : parseToLocaleText(location.level3?.text)
    );

    return addSplit(address, ", ");
  };
};

export const useStoreCache = () => {
  const { shouldExtendsFields } = useMarketConfig();
  const client = useApolloClient();
  return {
    getCacheListing,
    getCacheListings,
  };
  function getCacheListing(id: string) {
    const data = client.readQuery<
      ListingOfCacheQuery,
      ListingOfCacheQueryVariables
    >({
      query: LISTING_OF_CACHE_QUERY,
      variables: { id, shouldExtendsFields },
    });
    return data && data.listingOfCache;
  }
  function getCacheListings(ids: string[]) {
    const data = client.readQuery<
      ListingsOfCacheQuery,
      ListingsOfCacheQueryVariables
    >({
      query: LISTINGS_OF_CACHE_QUERY,
      variables: { ids, shouldExtendsFields },
    });
    return data && data.listingsOfCache;
  }
};

const DEFAULT_MODAL_KEY = "default_key";
export const useModal = () => {
  const [visible, setVisible] = React.useState<{ [key in string]: boolean }>({
    [DEFAULT_MODAL_KEY]: false,
  });
  return {
    closeModal: (key: string = DEFAULT_MODAL_KEY) =>
      setVisible({ ...visible, [key]: false }),
    showModal: (key: string = DEFAULT_MODAL_KEY) =>
      setVisible({ ...visible, [key]: true }),
    renderModal: (props: ModalProps & { key?: string }) => (
      <Modal
        {...props}
        visible={visible[props.key || DEFAULT_MODAL_KEY]}
        data-test="server-error"
        onClose={() => {
          setVisible({ ...visible, [props.key || DEFAULT_MODAL_KEY]: false });
          if (props.onClose) {
            props.onClose();
          }
        }}
      />
    ),
  };
};
